<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui'
import { z } from 'zod'

const schema = z.object({
  email: z.string().email()
})

type Schema = z.output<typeof schema>

const { $pb } = useNuxtApp()

const toast = useToast()

const loading = ref(false)


definePageMeta({
  layout: 'public',
  title: 'Forgot Password'
})

interface State {
  email: string;
  password: string;
}

const fields = [
  {
    name: 'email',
    type: 'text' as const,
    label: 'Email',
    placeholder: 'Enter your email'
  }
]

const doPasswordReset = async (payload: FormSubmitEvent<Schema>) => {
  try {
    loading.value = true
    toast.add({
      title: 'Forgot password',
      description: 'Requesting password reset token',
      icon: 'i-heroicons-lock-closed',
      color: 'info',
      duration: 2000
    })

    await $pb.collection('users').requestPasswordReset(payload.data.email)

    toast.add({
      title: 'Forgot password',
      description: 'Check your email to reset your password',
      icon: 'i-heroicons-paper-airplane',
      color: 'success',
      duration: 2000
    })

    navigateTo('/sign-in')
  } catch (error: any) {
    toast.add({
      title: 'Forgot password',
      description: error.message,
      icon: 'i-heroicons-exclamation-triangle',
      color: 'error',
      duration: 5000
    })
    // console.log(error)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="flex flex-col items-center justify-center min-h-screen">
    <UCard class="w-full max-w-sm">
      <UAuthForm
        :fields="fields"
        :ui="{ footer: 'text-center' }"
        :loading="loading"
        title="Forgot your password?"
        align="top"
        @submit="doPasswordReset"
      >
        <template #description>
          Do you have an account?
          <NuxtLink to="/sign-in" class="text-(--ui-primary) font-medium">
            Sign in.
          </NuxtLink>
        </template>

        <template #footer>
          By using this site, you agree to our
          <br>
          <ULink
            to="/legal/terms-of-service"
            class="text-(--ui-primary) font-medium"
          >
            Terms of Service
          </ULink>.
        </template>
      </UAuthForm>
    </UCard>
  </div>
</template>
